import { Camelizer } from '../utils';
import { PbxLineUpdateDetails } from '../types/Pbx';
import BaseAPI, { recoverFromNotFound } from './BaseAPI';

const PBX_BASE_URL = '/pbx/v5/pbxLines/';

export default class PbxAPI extends BaseAPI {
  buildURL = (...paths: string[]) => {
    const baseURL = this.host.tcUrl + PBX_BASE_URL;
    const url = baseURL + [...paths].join('/');
    return url;
  };

  @recoverFromNotFound()
  async assignPbxLine({
    pbxLineId,
    orgId,
    targetId,
    targetType,
  }: {
    pbxLineId: string;
    orgId: string;
    targetId: string;
    targetType: string;
  }) {
    const url = this.buildURL(pbxLineId, 'assign');
    const res = await this.httpClient.post(url, {
      headers: { 'TT-X-Organization-Key': orgId },
      data: { targetId, targetType },
    });

    return Camelizer.camelizeObject(res.data);
  }

  @recoverFromNotFound()
  async batchAssignUsersToNCLines({ targetIds, orgId }: { orgId: string; targetIds: string[] }) {
    const url = this.buildURL('nurseCall/assign');
    const res = await this.httpClient.post(url, {
      headers: { 'TT-X-Organization-Key': orgId },
      data: { targetIds },
    });

    return Camelizer.camelizeObject(res.data);
  }

  async retrieveNurseCallStatus({ orgId }: { orgId: string }) {
    const url = this.buildURL('nurseCall/status');
    const res = await this.httpClient.get(url, {
      headers: { 'TT-X-Organization-Key': orgId },
    });
    return res.data;
  }

  async batchUnAssignPbxLines({
    lineType,
    orgId,
    pbxLineIds,
    targetIds,
  }: {
    lineType: string;
    orgId: string;
    pbxLineIds: string[];
    targetIds?: string[];
  }) {
    const url =
      lineType === 'Nurse Call Users'
        ? this.buildURL('nurseCall/unassign')
        : this.buildURL('batchUnassign');
    const res = await this.httpClient.post(url, {
      headers: { 'TT-X-Organization-Key': orgId },
      data: lineType === 'Nurse Call Users' ? { targetIds } : { pbxLineIds },
    });

    return Camelizer.camelizeObject(res.data);
  }

  async unAssignPbxLine({
    lineType,
    orgId,
    pbxLineId,
    targetIds,
  }: {
    lineType: string;
    orgId: string;
    pbxLineId: string;
    targetIds?: string[];
  }) {
    const url =
      lineType === 'Nurse Call Users'
        ? this.buildURL('nurseCall/unassign')
        : this.buildURL(pbxLineId, 'unassign');
    const res = await this.httpClient.post(url, {
      headers: { 'TT-X-Organization-Key': orgId },
      data: lineType === 'Nurse Call Users' ? { targetIds } : {},
    });

    return Camelizer.camelizeObject(res.data);
  }

  async getPbxLineDetails({
    orgId,
    pbxLineId,
    detailed,
  }: {
    orgId: string;
    pbxLineId: string;
    detailed?: boolean;
  }) {
    const url = this.buildURL(pbxLineId);
    const headers = { 'TT-X-Organization-Key': orgId };
    const { data } = await this.httpClient.get(url, {
      headers,
      ...(detailed !== undefined ? { query: { detailed } } : {}),
    });
    return data.data;
  }

  async updatePbxLineDetails({
    orgId,
    pbxLineId,
    details,
  }: {
    orgId: string;
    pbxLineId: string;
    details: PbxLineUpdateDetails;
  }) {
    const url = this.buildURL(pbxLineId);
    const headers = { 'TT-X-Organization-Key': orgId };
    const { data } = await this.httpClient.put(url, {
      headers,
      data: details,
    });
    return data.data;
  }

  async getPbxLines({
    orgId,
    page,
    size,
    targetType,
    search,
    isAssigned,
    sortField = 'displayName',
    sortDirection = 'asc',
  }: {
    orgId: string;
    page: number;
    size: number;
    targetType: 'User' | 'Role' | 'NurseCall';
    search?: string;
    isAssigned?: boolean;
    sortField?: 'displayName' | 'extensionNumber';
    sortDirection?: 'asc' | 'desc';
  }) {
    const params = new URLSearchParams({
      orgToken: orgId,
      page: `${page}`,
      size: `${size}`,
    });
    if (isAssigned !== undefined) {
      params.set('isAssigned', `${isAssigned}`);
    } else if (targetType === 'NurseCall') {
      params.set('isAssigned', 'true');
    }
    params.append('sort', 'targetType,asc');
    params.append('sort', `${sortField},${sortDirection}`);
    params.set('targetType', targetType);
    if (search) {
      params.set('search', search);
    }
    const url = this.buildURL('pagination') + '?' + params;
    const headers = { 'TT-X-Organization-Key': orgId };
    const { data } = await this.httpClient.get(url, {
      headers,
    });
    return data;
  }

  async getPbxLinesByExtension({
    orgToken,
    extensionNumber,
    targetType,
  }: {
    orgToken: string;
    extensionNumber: string;
    targetType: string;
  }) {
    const params = new URLSearchParams({
      orgToken,
      extensionNumber,
      targetType,
    });
    const url = `${this.host.tcUrl}/pbx/v5/pbxLines?${params}`;
    const headers = { 'TT-X-Organization-Key': orgToken };
    const { data } = await this.httpClient.get(url, {
      headers,
    });
    return data;
  }

  async uploadPbxLinesViaCsv({
    email,
    file,
    headers,
    systemConfigurationId,
  }: {
    email: string;
    file: File;
    headers: {
      Authorization: string;
      'TT-X-Organization-Key': string;
    };
    systemConfigurationId: string;
  }) {
    const url = `${this.host.tcUrl}/pbx/v5/pbxLines/uploadAssign`;
    const formData = new FormData();
    formData.append('system_configuration_id', systemConfigurationId);
    formData.append('email', email);
    formData.append('file', file);

    const requestOptions = {
      method: 'POST',
      headers: headers,
      body: formData,
      credentials: 'include' as RequestCredentials,
    };

    fetch(url, requestOptions).catch((error) => {
      console.error('Error:', error);
    });
  }

  async getPbxSystems({
    currentOrganizationId,
    containNurseCall,
  }: {
    currentOrganizationId: string;
    containNurseCall: boolean;
  }) {
    const res = await this.httpClient.get(
      `${this.host.tcUrl}/pbx/v5/pbxSystems?containNurseCall=${containNurseCall}`,
      {
        headers: { 'TT-X-Organization-Key': currentOrganizationId },
      }
    );
    return res.data.data;
  }

  async uploadPbxDirectoryViaCsv({
    file,
    headers,
  }: {
    file: File;
    headers: {
      Authorization: string;
      'TT-X-Organization-Key': string;
    };
  }) {
    const url = `${this.host.tcUrl}/pbx/v5/pbxDirectory/upload`;
    const formData = new FormData();
    formData.append('file', file);

    const requestOptions = {
      method: 'POST',
      headers: headers,
      body: formData,
      credentials: 'include' as RequestCredentials,
    };

    fetch(url, requestOptions).catch((error) => {
      console.error('Error:', error);
    });
  }

  async getPbxDirectory({
    orgToken,
    page = 1,
    size = 20,
    sort = 'displayName,asc',
    search = '',
  }: {
    orgToken: string;
    page?: number;
    size?: number;
    sort?: string;
    search?: string;
  }) {
    const params = new URLSearchParams({
      orgToken,
      page: `${page}`,
      size: `${size}`,
      sort,
    });
    if (search) {
      params.set('search', search);
    }
    const url = `${this.host.tcUrl}/pbx/v5/pbxDirectory/pagination?${params}`;
    const headers = { 'TT-X-Organization-Key': orgToken };
    const { data } = await this.httpClient.get(url, { headers });
    return data;
  }

  async createPbxDirectoryEntry({
    orgToken,
    entry,
  }: {
    orgToken: string;
    entry: {
      displayName: string;
      phoneNumber: string;
      location?: string;
      department?: string;
      title?: string;
    };
  }) {
    const url = `${this.host.tcUrl}/pbx/v5/pbxDirectory`;
    const headers = { 'TT-X-Organization-Key': orgToken };
    const { data } = await this.httpClient.post(url, { headers, data: entry });
    return data;
  }

  async updatePbxDirectoryEntry({
    orgToken,
    id,
    entry,
  }: {
    orgToken: string;
    id: string;
    entry: {
      displayName: string;
      phoneNumber: string;
      location?: string;
      department?: string;
      title?: string;
      organizationId?: number;
    };
  }) {
    const url = `${this.host.tcUrl}/pbx/v5/pbxDirectory/${id}`;
    const headers = { 'TT-X-Organization-Key': orgToken };
    const { data } = await this.httpClient.put(url, { headers, data: entry });
    return data;
  }

  async deletePbxDirectoryEntry({ id }: { id: string }) {
    const url = `${this.host.tcUrl}/pbx/v5/pbxDirectory/${id}`;
    const { data } = await this.httpClient.del(url, {});
    return data;
  }

  async batchDeletePbxDirectoryEntries({ orgToken, ids }: { orgToken: string; ids: number[] }) {
    const url = `${this.host.tcUrl}/pbx/v5/pbxDirectory/batch`;
    const headers = { 'TT-X-Organization-Key': orgToken };
    const { data } = await this.httpClient.del(url, { headers, data: ids });
    return data;
  }

  async getPatientLink(roomName: string, memberId: string, organizationId: string) {
    const res = await this.httpClient.get(`/v2/patient_link/${roomName}/${memberId}`, {
      headers: { 'TT-X-Organization-Key': organizationId },
    });

    return res.data;
  }
}
